/**
 * PHP-4126: the draft bar's "Preview". The website runtime serves the live site with the saved,
 * unpublished chat button in place of the published one (a 30-minute preview link), and the portal
 * shows it here. Nothing is published until the customer presses Publish.
 */
import { useQuery } from "@tanstack/react-query";

import { useOrganization } from "@/auth/organization";
import { Button, ErrorAlert, Modal } from "@/components/ui";
import { portal } from "@/lib/api";
import type { Site } from "@/lib/types";

import { frameable, LiveSiteFrame } from "../websites/LiveSiteFrame";

/** Preview links work for 30 minutes; ask for a fresh one well before that. */
const LINK_STALE_MS = 20 * 60 * 1000;

export function WhatsAppPreviewDialog({
  open,
  onClose,
  site,
  siteCount,
  settingsSavedAt,
  onPublish,
  publishPending,
}: {
  open: boolean;
  onClose: () => void;
  site: Site;
  /** Publishing updates every website of the organization, not only the one shown. */
  siteCount: number;
  /** The saved settings the preview shows; a new save asks for a new link. */
  settingsSavedAt: string | null;
  onPublish: () => void;
  publishPending: boolean;
}) {
  const { orgId } = useOrganization();
  const preview = useQuery({
    queryKey: ["whatsapp-preview", orgId, site.id, settingsSavedAt],
    queryFn: () => portal.previewWhatsapp(orgId, site.id),
    enabled: open,
    staleTime: LINK_STALE_MS,
    gcTime: LINK_STALE_MS,
    retry: false,
  });
  const url = preview.data?.url;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Preview of your draft · not live"
      subtitle="Your website with the chat button you saved, before you publish it"
      width={760}
      note={
        siteCount > 1
          ? `Publishing updates all ${siteCount} of your websites. This preview shows ${site.name}.`
          : undefined
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="brand"
            loading={publishPending}
            onClick={() => {
              onPublish();
              onClose();
            }}
          >
            {publishPending ? "Publishing…" : "Publish to live site"}
          </Button>
        </>
      }
    >
      <p className="rounded-lg bg-[#fce7c6] px-3 py-2 text-xs font-semibold text-fg">
        Preview only. Visitors don’t see this until you publish.
      </p>
      <div className="overflow-hidden rounded-lg border border-border">
        <div className="flex items-center gap-2 border-b border-border bg-muted px-3 py-2">
          <span aria-hidden className="flex gap-1">
            <span className="size-2 rounded-full bg-border" />
            <span className="size-2 rounded-full bg-border" />
            <span className="size-2 rounded-full bg-border" />
          </span>
          <span className="min-w-0 flex-1 truncate rounded bg-surface px-2 py-0.5 text-[11.5px] text-fg-subtle">
            {site.hostname}
          </span>
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 text-[11.5px] font-semibold text-brand-500 hover:text-brand-600"
            >
              Open in a new tab ↗
            </a>
          )}
        </div>
        {preview.isError ? (
          <div className="p-4">
            <ErrorAlert error={preview.error} />
          </div>
        ) : url && !frameable(url) ? (
          <p className="p-6 text-center text-sm text-fg-subtle">
            This preview can’t be shown inside the dashboard. Use “Open in a new tab”.
          </p>
        ) : url ? (
          <LiveSiteFrame
            url={url}
            title={`Preview of ${site.name} with your draft chat button`}
            interactive
          />
        ) : (
          <div
            role="status"
            className="flex aspect-[16/10] items-center justify-center text-sm text-fg-subtle"
          >
            Preparing the preview…
          </div>
        )}
      </div>
      <p className="text-xs leading-[1.5] text-fg-subtle">
        This is your live website with the chat button you saved. Clicking the button opens your chat link.
        The preview link works for 30 minutes.
      </p>
    </Modal>
  );
}
